import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Observable, of } from 'rxjs'
import * as moment from 'moment'
import {
  ExpenseView,
  CreateExpensePayload,
  ExpenseResponse,
  IExpenseFilters,
  UpdateExpensePayload,
  UpdateExpenseSettingsPayload,
  ExpenseApprovalSettings,
  FetchExpenseStatsReportParams,
  ExpenseCategoryReportItem,
  toExpenseView,
} from './expense.model'
import { catchError, concatMap, map, mergeMap, tap, toArray } from 'rxjs/operators'
import * as R from 'ramda'
import * as lodash from 'lodash'
import { Toaster } from '../../services/toaster.service'
import { isSomething } from '../../functions'
import { Pagination } from '../../types/pagination'

@Injectable()
export class ExpenseService {
  constructor(private http: HttpClient, private toast: Toaster) {}

  all(request?: IExpenseFilters, pagination?: Pagination): Observable<ExpenseView[]> {
    const url = `@api/expense/all/`

    pagination = pagination || { skip: 0, limit: 1000 }

    const filters: {
      due_from?: number
      due_to?: number
      method?: string
      status?: string | number
      rentals?: string | string[]
      recurring?: 0 | 1
    } = {}

    if (request) {
      if (isSomething(request.period) && request.period.from > 0 && request.period.to > 0) {
        const dayStart = moment.unix(request.period.from).startOf('day').unix()
        const dayEnd = moment.unix(request.period.to).endOf('day').unix()

        filters.due_from = dayStart
        filters.due_to = dayEnd
      }
      if (isSomething(request.method) && request.method !== 'all') {
        filters.method = request.method
      }
      if (isSomething(request.status) && request.status !== 'all') {
        filters.status = request.status
      }
      if (isSomething(request.rentals)) {
        filters.rentals = request.rentals
      }
      if (request.recurring === 'recurring') {
        filters.recurring = 1
      } else if (request.recurring === 'not_recurring') {
        filters.recurring = 0
      }
    }

    // sor by expense date
    const params = new HttpParams({
      fromObject: { skip: pagination.skip + '', limit: pagination.limit + '', sort: 'date', direction: '-1' },
    })

    return this.http
      .post<ExpenseResponse[]>(url, { ...filters }, { params })
      .pipe(map((responses) => this.mapExpenses(responses)))
  }

  get(id: string): Observable<ExpenseView> {
    const url = `@api/expense/${id}`

    return this.http.get<ExpenseResponse>(url).pipe(map((response) => toExpenseView(response)))
  }

  getByIds(ids: string[]): Observable<ExpenseView[]> {
    return of(...ids).pipe(
      concatMap((id) => this.get(id).pipe(catchError(() => of(null)))),
      toArray(),
      map((items) => items.filter((t) => !lodash.isNil(t)) as ExpenseView[])
    )
  }

  create(request: CreateExpensePayload): Observable<ExpenseView> {
    const url = '@api/expense/'

    return this.http.post<ExpenseResponse>(url, request).pipe(
      tap((response) => this.warnRecurrenceSyncFailed(response)),
      map((response) => toExpenseView(response)),
      tap(() => this.toast.success($localize`Expense created successfully.`))
    )
  }

  createBatch(request: CreateExpensePayload[]): Observable<ExpenseView[]> {
    const url = '@api/expenses/'

    return this.http.post<ExpenseResponse[]>(url, request).pipe(map((responses) => this.mapExpenses(responses)))
  }

  update(expenseId: string, payload: UpdateExpensePayload): Observable<ExpenseView> {
    const url = `@api/expense/${expenseId}`

    return this.http.put<ExpenseResponse>(url, payload).pipe(
      tap((response) => this.warnRecurrenceSyncFailed(response)),
      map((response) => toExpenseView(response)),
      tap(() => this.toast.success($localize`Expense updated successfully.`))
    )
  }

  // the API embeds the expense-service push result; the expense itself is
  // saved either way, but a failed push means the recurrence didn't sync
  private warnRecurrenceSyncFailed(response: ExpenseResponse) {
    if (response?.expenseApi?.http_code >= 400) {
      this.toast.warning(
        $localize`The expense was saved, but its recurring schedule could not be synced. Please re-save the expense or contact support.`
      )
    }
  }

  delete(expenseId: string): Observable<ExpenseView> {
    const url = `@api/expense/delete/${expenseId}`

    return this.http.delete<ExpenseResponse>(url).pipe(
      map((response) => toExpenseView(response)),
      tap(() => this.toast.success($localize`Expense deleted successfully.`))
    )
  }

  deleteExpenses(expenseIds: string[]): Observable<string[]> {
    return of(...expenseIds).pipe(
      mergeMap((id) => this.http.delete(`@api/expense/delete/${id}`).pipe(map(() => id))),
      toArray(),
      tap(() => this.toast.success($localize`Expenses deleted successfully!`))
    )
  }

  private mapExpenses(expenses: ExpenseResponse[]): ExpenseView[] {
    return R.map((r: ExpenseResponse) => toExpenseView(r), expenses)
  }

  getCategoryReports(
    params: Omit<FetchExpenseStatsReportParams, 'statuses' | 'approval_statuses'>
  ): Observable<Record<string, ExpenseCategoryReportItem[]>> {
    return this.http
      .post<
        {
          _id: { date: string; category: string }
          amount: number
        }[]
      >('@api/reports/expenses/dates', params)
      .pipe(
        map((items) => items.map(({ _id, amount }) => ({ category: _id.category, date: _id.date, amount }))),
        map((items) => lodash.groupBy(items, 'category'))
      )
  }

  getStatusReports(params: Omit<FetchExpenseStatsReportParams, 'approval_statuses' | 'categories'>) {
    return this.http
      .post<{ _id: number; amount: number }[]>('@api/reports/expenses/dates', params)
      .pipe(map((items) => items.map((t) => ({ status: t._id, amount: t.amount }))))
  }

  getApprovalReports(params: Omit<FetchExpenseStatsReportParams, 'statuses' | 'categories'>) {
    return this.http
      .post<{ _id: string; amount: number }[]>('@api/reports/expenses/dates', params)
      .pipe(map((items) => items.map((t) => ({ status: t._id, amount: t.amount }))))
  }

  getSettings() {
    const url = `@api/expensesettings`

    return this.http.get<ExpenseApprovalSettings>(url)
  }

  updateSettings(payload: UpdateExpenseSettingsPayload) {
    const url = `@api/expensesettings`

    return this.http.put<ExpenseApprovalSettings>(url, payload)
  }
}
